import { type ButtonHTMLAttributes, forwardRef } from 'react'

import { Slot } from '@radix-ui/react-slot'

import { cn } from '../../lib/utils'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean
  variant?: 'ghost' | 'default' | 'outline' | 'shuttle' | 'neon' | 'glass' | 'gradient' | 'kinetic'
  size?: 'default' | 'sm' | 'icon' | 'lg' | 'xl'
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'default', size = 'default', asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : ('button' as any)
    return (
      <Comp
        className={cn(
          'inline-flex transform-gpu items-center justify-center whitespace-nowrap rounded-md font-medium transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
          {
            // Default variant
            'bg-gray-900 text-gray-50 shadow hover:bg-gray-900/90 dark:bg-gray-50 dark:text-gray-900 dark:hover:bg-gray-50/90':
              variant === 'default',

            // Ghost variant
            'hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-800 dark:hover:text-gray-50':
              variant === 'ghost',

            // Outline variant (used throughout the site)
            'border border-gray-300 bg-transparent text-gray-900 hover:bg-gray-100 focus-visible:ring-gray-400 dark:border-gray-700 dark:text-gray-50 dark:hover:bg-gray-800 dark:focus-visible:ring-gray-600':
              variant === 'outline',

            // Cloud Shuttle brand variants
            'transform bg-shuttle-red text-white shadow-lg hover:scale-105 hover:bg-shuttle-red-light hover:shadow-xl focus-visible:ring-shuttle-red':
              variant === 'shuttle',

            // Neon variant with glow effect
            'transform animate-glow-pulse bg-shuttle-red text-white shadow-lg neon-glow hover:scale-105 hover:bg-shuttle-red-light hover:shadow-xl focus-visible:ring-shuttle-red':
              variant === 'neon',

            // Glassmorphism variant
            'transform border border-white/20 text-white shadow-lg backdrop-blur-xl glass-card hover:scale-105 hover:shadow-xl focus-visible:ring-white/50':
              variant === 'glass',

            // Gradient variant
            'transform animate-gradient-shift bg-shuttle-gradient text-white shadow-lg hover:scale-105 hover:shadow-xl focus-visible:ring-shuttle-purple':
              variant === 'gradient',

            // Kinetic (Stitch) variant
            'rounded-none bg-kinetic-primary-container font-headline font-bold uppercase tracking-[0.18em] text-kinetic-on-primary shadow-[0_0_0_1px_rgba(0,240,255,0.15)] hover:shadow-[0_0_15px_rgba(0,240,255,0.55)] focus-visible:ring-kinetic-primary-container focus-visible:ring-offset-kinetic-surface active:scale-[0.99]':
              variant === 'kinetic',

            // Size variants
            'h-9 px-4 text-sm': size === 'default',
            'h-8 px-3 text-xs': size === 'sm',
            'h-9 w-9': size === 'icon',
            'h-12 px-6 text-base': size === 'lg',
            'h-14 px-8 text-lg': size === 'xl',
          },
          className
        )}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = 'Button'

export { Button }
